import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-final-form';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import type { FormState } from 'final-form';
import { sendEvent } from '@alfa-bank/site-helpers-common/browser/send-event';

import {
    getFormData,
    validate,
    formatClientData,
    isOneField,
    formatResponseDate,
    isEmptyObject,
} from './utils';
import type { TFormDraftFirstStep } from './models';
import { draftFirstStepApi } from './api';
import type { TResponseFormatData, TFirstStepData } from './models/data';
import {
    DEBOUNCE_DELAY,
    FORM_CHANGED_SUBSCRIPTION,
    FORM_SUBMITTING_SUBSCRIPTION,
} from './constants';
import { onSentryCaptureException } from './utils/sentry';
import { reverseObjectKeysToValue } from './utils/formatters';

export const FormDraftFirstStep = ({
    analytics,
    disableDeleteOnSubmit = false,
    mapFields,
    sentryCaptureException,
    setIsOneFieldFio,
}: TFormDraftFirstStep) => {
    // Данные из ответа сервера
    const [dataFromService, setDataFromService] = useState<TResponseFormatData | null>(null);
    // Данные из формы на клиенте
    const [dataFromClient, setDataFromClient] = useState<TFirstStepData | null>(null);
    const [isFormReady, setIsFormReady] = useState(false);
    const form = useForm();

    // Мапилка полей в обратную сторону с сервера на клиент
    const reversedMapFields = reverseObjectKeysToValue(mapFields);

    const onInitializeForm = useCallback(() => {
        if (dataFromService) {
            // Добавляем данные с бэка в форму
            form.initialize(currentFormValues => ({ ...currentFormValues, ...dataFromService }));
            /** Запускаем валидацию полей, которые пришли с бэка*/
            validate({ form, values: dataFromService });
        }
    }, [dataFromService, form]);

    const onSubmit = useCallback(
        ({ submitting }: FormState<TFirstStepData>) => {
            /** Удаляем первый шаг при submit */
            if (submitting && !disableDeleteOnSubmit) draftFirstStepApi.delete();
        },
        [disableDeleteOnSubmit]
    );

    useEffect(() => {
        draftFirstStepApi
            .get()
            .then(response => {
                setDataFromService(formatResponseDate(response.data, reversedMapFields));
            })
            .catch(onSentryCaptureException(sentryCaptureException));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!dataFromService) return;

        /**
         * Если получили ФИО полностью, или все поля ФИО отсутствуют.
         * Оставляем ФИО в одном поле. В противном случае, делим на 3 поля.
         */
        setIsOneFieldFio(isOneField(dataFromService));
        /**
         * setIsOneFieldFio & setIsFormReady вызываем совместно для батчинга.
         * Так будем уверены, что форма отобразилась с нужным количеством полей ФИО.
         */
        setIsFormReady(true);
    }, [dataFromService, setIsOneFieldFio]);

    useEffect(() => {
        /** Инициализируем форму. setTimeout решает проблему с отображением данных в интерфейсе  */
        if (isFormReady && dataFromService) {
            const timeout = setTimeout(onInitializeForm);
            /**
             * Убеждаемся, что после вызова formatResponseDate(а именно в pipe функции filterEmptyValues)
             * не получаем пустой объект чтобы не отправлять данные в аналитику
             */
            const validForSendAnalytics = !isEmptyObject(dataFromService);
            if (analytics && validForSendAnalytics) {
                sendEvent(analytics);
            }

            /** При unmount - чистим timeout */
            return () => clearTimeout(timeout);
        }
    }, [isFormReady, onInitializeForm, analytics, dataFromService]);

    useEffect(
        () =>
            /** Подписываемся в форме на изменение: значений и ошибок.*/
            form.subscribe(({ active, ...args }) => {
                /** Проверяем, что фокус не установлен ни в одно из полей, чтобы при расфокусе прошла валидация */
                if (!active) {
                    /** Добавляем поля на отправку, в которых нет ошибок */
                    setDataFromClient(getFormData(args));
                }
            }, FORM_CHANGED_SUBSCRIPTION),
        [dataFromService, form]
    );

    useEffect(() => {
        /** Добавляем debounce на отправку, чтобы при каждом вводе не отсылать данные на бэк */
        const debouncedFunc = debounce(draftFirstStepApi.put, DEBOUNCE_DELAY);
        /** Проверяем, что есть данные на отправку. */
        if (isEmpty(dataFromClient) || !dataFromClient) return;

        /** Преобразуем данные для отправки */
        const requestData = formatClientData(dataFromClient, mapFields);
        const responseData = formatClientData({ ...dataFromService }, mapFields);

        if (dataFromService && isEqual(requestData, responseData)) {
            return;
        }
        /** Обновляем данные на сервере */
        debouncedFunc({ data: requestData });

        return debouncedFunc.cancel;
    }, [dataFromService, dataFromClient, mapFields]);

    /** Подписываемся на submit формы */
    useEffect(() => form.subscribe(onSubmit, FORM_SUBMITTING_SUBSCRIPTION), [form, onSubmit]);

    return null;
};
